import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { authApi } from "@/services/adminApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function SignupPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    full_name: "",
    employee_id: "",
    department: "",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await authApi.signup(formData);
      setSuccess(true);
    } catch (err) {
      const detail = err.response?.data?.detail;
      // Handle Pydantic validation errors (array of objects)
      if (Array.isArray(detail)) {
        const messages = detail.map((e) => e.msg || e.message || JSON.stringify(e)).join("; ");
        setError(messages);
      } else if (typeof detail === "string") {
        setError(detail);
      } else {
        setError("Signup failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-earth-neutral">
        <Card className="w-full max-w-md shadow-lg">
          <CardHeader className="space-y-4">
            {/* Logo */}
            <div className="flex justify-center">
              <img
                src="/impleo-logo.svg"
                alt="Impleo"
                className="h-10 w-auto"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-center text-green-600">
              Request Submitted
            </CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-muted-foreground">
              Your admin access request has been submitted successfully.
            </p>
            <p className="text-muted-foreground">
              You will be able to log in once your request is approved by an
              administrator.
            </p>
          </CardContent>
          <CardFooter>
            <Link to="/login" className="w-full">
              <Button variant="outline" className="w-full">
                Back to Login
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-earth-neutral">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-4 pb-2">
          {/* Logo */}
          <div className="flex justify-center">
            <img
              src="/impleo-logo.svg"
              alt="Impleo"
              className="h-10 w-auto"
            />
          </div>
          <div className="space-y-1">
            <CardTitle className="text-2xl font-bold text-center">
              Request Admin Access
            </CardTitle>
            <CardDescription className="text-center">
              Submit your details for approval to join Impleo Admin
            </CardDescription>
          </div>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {error && (
              <div className="rounded-md bg-destructive/15 p-3 text-sm text-destructive">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="full_name">Full Name</Label>
              <Input
                id="full_name"
                name="full_name"
                type="text"
                placeholder="John Doe"
                value={formData.full_name}
                onChange={handleChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Work Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="john.doe@impleo.ai"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                minLength={12}
              />
              <p className="text-xs text-muted-foreground">
                Min 12 chars with uppercase, lowercase, number, and special character
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="employee_id">Employee ID</Label>
                <Input
                  id="employee_id"
                  name="employee_id"
                  type="text"
                  placeholder="EMP-001"
                  value={formData.employee_id}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="department">Department</Label>
                <select
                  id="department"
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  required
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Select...</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Product">Product</option>
                  <option value="Sales">Sales</option>
                  <option value="Support">Support</option>
                  <option value="Operations">Operations</option>
                </select>
              </div>
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Submitting..." : "Submit Request"}
            </Button>

            <Link
              to="/login"
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              Already have access? Sign in
            </Link>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
}

export default SignupPage;
